import type { OnboardingPreferences } from "@albertine/types";
import {
    type ThemePreference,
    type ThemePreferenceScoreMap,
    zeroThemePreferenceScoreMap,
} from "@albertine/types/theme";
import { z } from "zod";
import type { TargetingPreferences } from "../types/TargetingPreferences";
import { cityGuideKey } from "./catalog.util";
import { includeTypeOf } from "./zod.util";

const TopCityThemeLocation = z.object({
    city: z.string(),
    country: z.string(),
    countryCode: z.string(),
});

type TopCityThemeLocation = z.infer<typeof TopCityThemeLocation>;

export function resolveTopCityThemeLocations(
    preferences?: TargetingPreferences,
): TopCityThemeLocation[] {
    if (preferences?.topCities) {
        return preferences.topCities
            .filter(includeTypeOf(TopCityThemeLocation))
            .map((c) => ({
                city: c.city,
                country: c.country,
                countryCode: c.countryCode,
            }));
    }
    return [];
}

export function resolveTopCities(preferences?: TargetingPreferences): string[] {
    return resolveTopCityThemeLocations(preferences).map(cityGuideKey);
}

function recommendationCategoryToThemePreference(
    category: string,
): ThemePreference | undefined {
    switch (category) {
        case "travel":
            return "travel";
        case "wellness":
            return "wellness";
        case "gastronomy":
            return "dining";
        case "sportsEvents":
            return "sportsEvents";
        case "culturalEvents":
            return "culturalEvents";
        case "localExperiences":
            return "localExperiences";
        default:
            return undefined;
    }
}

export function resolveThemePreferences(
    preferences?: OnboardingPreferences,
): ThemePreferenceScoreMap {
    const result: ThemePreferenceScoreMap = zeroThemePreferenceScoreMap();
    if (preferences?.recommendations) {
        preferences.recommendations.forEach((r) => {
            const category = recommendationCategoryToThemePreference(r.category);
            if (!category) {
                return;
            }
            switch (r.feedback) {
                case "neutral":
                    result[category] = 0;
                    break;
                case "like":
                    result[category] = 1;
                    break;
                case "heart":
                    result[category] = 2;
                    break;
                case "dislike":
                    result[category] = -1;
                    break;
                default:
            }
        });
    }
    return result;
}

export function mergeThemePreferences(targeting?: {
    preferences?: Record<string, number> | null;
}): ThemePreferenceScoreMap {
    return { ...zeroThemePreferenceScoreMap(), ...targeting?.preferences };
}
